// Client-side reader-copy fallbacks. The enriched exporter and the demo bundle ship
// these strings inside the bundle; when a (plainer) real bundle omits them, bundle.ts
// fills from here so the UI never shows raw axis jargon or bare domains.

import type { NumericAxis, Scores } from "./types";

// ---- outlet domain -> human display name (spec §9 open item #3) ----
const OUTLET_DISPLAY: Record<string, string> = {
  "miamiherald.com": "Miami Herald",
  "cbsmiami.com": "CBS Miami",
  "local10.com": "Local 10 News",
  "nbcconnecticut.com": "NBC Connecticut",
  "wfsb.com": "WFSB",
  "courant.com": "Hartford Courant",
  "nbcnews.com": "NBC News",
  "cnn.com": "CNN",
  "foxnews.com": "Fox News",
  "nypost.com": "New York Post",
  "wsvn.com": "WSVN 7News",
  "abcnews.go.com": "ABC News",
  "usatoday.com": "USA Today",
  "washingtonpost.com": "The Washington Post",
  "nytimes.com": "The New York Times",
  "theguardian.com": "The Guardian",
  "apnews.com": "Associated Press",
  "reuters.com": "Reuters",
};

const SUFFIXES = new Set(["com", "org", "net", "news", "go", "co", "us", "tv"]);

/** Best-effort human label for an outlet domain (title-cases the host when unknown). */
export function outletDisplay(domain: string): string {
  if (!domain) return "";
  if (OUTLET_DISPLAY[domain]) return OUTLET_DISPLAY[domain];
  const host = domain.replace(/^https?:\/\//, "").replace(/^www\./, "");
  const parts = host.split(".").filter((p) => !SUFFIXES.has(p));
  const core = parts[0] || host;
  return core
    .replace(/[-_]/g, " ")
    .split(" ")
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
    .join(" ");
}

/** Short 2–3 char chip token for the outlet avatar. */
export function outletShort(domain: string): string {
  const known: Record<string, string> = {
    "miamiherald.com": "MH", "cbsmiami.com": "CBS", "local10.com": "L10",
  };
  return known[domain] || (domain || "").slice(0, 3).toUpperCase();
}

// ---- axis reader labels ----
export const AXIS_LABELS: Record<NumericAxis, string> = {
  civ_agency: "Civilian agency",
  civ_char: "Civilian portrayal",
  off_agency: "Officer agency",
  off_char: "Officer portrayal",
  force_justification: "Force justified",
  systemic_context: "Systemic context",
  sourcing_balance: "Source balance",
  moral_lean: "Moral lean",
};

// scale-endpoint labels (low | high)
export const AXIS_ENDS: Record<NumericAxis, [string, string]> = {
  civ_agency: ["Passive", "Active aggressor"],
  civ_char: ["Negative", "Sympathetic"],
  off_agency: ["Hidden", "Named actor"],
  off_char: ["Negative", "Sympathetic"],
  force_justification: ["Questioned", "Justified"],
  systemic_context: ["None", "Rich"],
  sourcing_balance: ["Police-sourced", "Multi-sourced"],
  moral_lean: ["Toward civilian", "Toward police"],
};

// per-axis lean phrase given the article's signed value
export function leanLabel(axis: NumericAxis, value: number): string {
  const [lo, hi] = AXIS_ENDS[axis];
  if (axis === "systemic_context") {
    return value <= 0 ? "Gives no broader context" : "Adds systemic context";
  }
  if (value === 0) return `Balanced on ${AXIS_LABELS[axis].toLowerCase()}`;
  const dir = value > 0 ? hi.toLowerCase() : lo.toLowerCase();
  return `Leans ${dir}`;
}

// ---- categorical reader copy (from target.scores) ----
export function categoricals(scores: Scores | undefined) {
  if (!scores) return [];
  const out: { label: string; value: string; note: string }[] = [];
  const agent = scores.agent_of_force;
  out.push({
    label: "Who acted",
    value: agent === "officer_agent" ? "Officer named" : agent === "passive_no_agent" ? "Agent hidden"
      : agent === "civilian_blamed" ? "Civilian blamed" : "Mutual",
    note: `agent_of_force = ${agent}: ${agent === "officer_agent" ? "the framing names the officer as the actor." : "the framing names no clear actor for the force used."}`,
  });
  const stance = scores.police_claim_stance;
  out.push({
    label: "Police account",
    value: stance === "asserted_fact" ? "Stated as fact" : stance === "attributed" ? "Attributed" : "Contested",
    note: `police_claim_stance = ${stance}: ${stance === "asserted_fact" ? "police claims appear without attribution." : "police claims are attributed or contested."}`,
  });
  const hp = scores.headline_perspective;
  out.push({
    label: "Headline frame",
    value: hp === "police" ? "Police view" : hp === "civilian" ? "Civilian view" : "Neutral",
    note: `headline_perspective = ${hp}: the headline leads with the ${hp} account.`,
  });
  return out;
}

// ---- device → reader phrase (for best-effort device highlight tooltips) ----
export const DEVICE_LABELS: Record<string, string> = {
  euphemism_officer_involved: "Names no agent; the phrase hides who fired.",
  civilian_armed_mentioned: "Labels the civilian as armed, often before any finding.",
  civilian_criminal_record_mentioned: "Raises the civilian's record, coloring the portrayal.",
  civilian_unarmed_mentioned: "Notes the civilian was unarmed.",
  civilian_race_mentioned: "Mentions the civilian's race.",
  civilian_mental_health_mentioned: "Raises the civilian's mental health.",
  civilian_named: "Names the civilian as a person.",
  civilian_family_humanizing: "Includes humanizing detail from family.",
  officer_feared_for_life: "Frames the force through the officer's stated fear.",
};
